import { GridCellParams } from '@mui/x-data-grid';
import { Typography } from '@mui/material';
import { getConnectionColor } from '../utils/tableHelpers';
import { addDays } from 'date-fns';
import { WeatherTableRow } from '../types';
import { useLocations } from 'features/locations/api/getLocations';
import { useDishes } from 'features/dishes/api/getDishes';
import { useDemoExpirations } from 'features/systemModules/api/getDemoExpirations';
import { formatInTimeZone } from 'date-fns-tz';
import { useUserDateTime } from '../../timezone/hooks/useUserDateTime';

const useSetupWeatherTable = (locationName: string, dish: string) => {
  const { data: locations } = useLocations({});
  const { data: dishes } = useDishes({});
  const { data: demoExpirationsInDays } = useDemoExpirations({});
  const { dateFormatWithZeros, timezone } = useUserDateTime();

  const expirationTimestamp = formatInTimeZone(
    addDays(
      new Date(),
      demoExpirationsInDays ? demoExpirationsInDays['WEATHER'] : 0
    ),
    timezone,
    dateFormatWithZeros
  );

  const columns = [
    { field: 'id', hide: true },
    {
      field: 'name',
      headerName: 'Location',
      width: 180,
      headerClassName: `system-page--header`,
    },
    {
      field: 'coordinates',
      headerName: 'Coordinates',
      width: 180,
      headerClassName: `system-page--header`,
    },
    {
      field: 'dishes',
      headerName: 'Dishes',
      width: 180,
      headerClassName: `system-page--header`,
    },
    {
      field: 'expiration',
      headerName: 'Expiration',
      width: 180,
      headerClassName: `system-page--header`,
    },
    {
      field: 'enabled',
      headerName: 'Status',
      width: 180,
      headerClassName: `system-page--header`,
      renderCell: (params: GridCellParams) => {
        const matchingLocation = locations?.find(
          (item: any) => item?.id === params.row.id
        );
        return (
          <Typography
            variant='subtitle2'
            style={{ color: getConnectionColor(matchingLocation) }}
          >
            {matchingLocation?.enabled ? 'Enabled' : 'Disabled'}
          </Typography>
        );
      },
    },
  ];

  const rows: WeatherTableRow[] = [];

  if (locations) {
    for (const location of locations) {
      if (location) {
        if (
          !location?.name.toLowerCase().includes(locationName.toLowerCase())
        ) {
          continue;
        }
      }

      const dishesByLocation = dishes?.filter(
        (dish) => dish.location_id === location.id
      );

      const dishNames = dishesByLocation?.map((dish) => dish.name);

      const dishString =
        dishNames && dishNames.length > 0 ? dishNames.join(', ') : 'None';

      if (dish) {
        if (!dishString.toLowerCase().includes(dish.toLowerCase())) {
          continue;
        }
      }

      const formattedRow = {
        ...location,
        coordinates: `${location.coord_1}, ${location.coord_2}`,
        dishes: dishString,
        expiration: expirationTimestamp,
        enabled: !!location?.enabled,
      };

      rows.push(formattedRow);
    }
  }

  return { columns, rows };
};

export default useSetupWeatherTable;
